/**
 * Limits & Fees Articles — Spanish
 */

import type { HelpArticle } from "../../../types";

export const LIMITS_FEES_ARTICLES_ES: HelpArticle[] = [
  {
    id: "lf-001",
    slug: "transaction-limits-overview",
    title: "Resumen de límites de transacción",
    description: "Entendiendo los límites de tu cuenta",
    category: "limits-fees",
    popular: true,
    order: 1,
    keywords: ["límites", "máximo", "mínimo", "restricciones", "topes"],
    content: `
# Resumen de límites de transacción

Tus límites de transacción dependen de tu nivel de verificación e historial de cuenta.

## Categorías de límites

### Límites de depósito
- Mínimo: $10 por transacción
- Máximo: Varía según nivel

### Límites de pago
- Límite por transacción
- Límite agregado diario
- Límite agregado mensual

### Límites de conversión
- Límite por conversión
- Volumen de conversión diario
- Volumen de conversión mensual

## Límites por nivel

### Nivel Estándar
| Tipo de límite | Monto |
|----------------|-------|
| Por depósito | $25,000 |
| Por pago | $10,000 |
| Pagos diarios | $25,000 |
| Volumen mensual | $100,000 |

### Nivel Avanzado
| Tipo de límite | Monto |
|----------------|-------|
| Por depósito | $100,000 |
| Por pago | $50,000 |
| Pagos diarios | $100,000 |
| Volumen mensual | $500,000 |

### Nivel Enterprise
| Tipo de límite | Monto |
|----------------|-------|
| Por depósito | Personalizado |
| Por pago | Personalizado |
| Pagos diarios | Personalizado |
| Volumen mensual | Personalizado |

## Ver tus límites

1. Ve a Ajustes > Límites
2. Ver nivel actual
3. Ver límites restantes
4. Seguimiento del uso en el tiempo

## Cuándo se reinician los límites

- **Límites diarios**: Medianoche UTC
- **Límites mensuales**: Primero de cada mes
- **Por transacción**: Inmediatamente después de completarse

## Límites temporales

Pueden aplicarse límites adicionales para:
- Cuentas nuevas (primeros 30 días)
- Primeras transacciones grandes
- Después de cambios en la cuenta
- Revisiones de seguridad
    `.trim(),
  },
  {
    id: "lf-002",
    slug: "increasing-your-limits",
    title: "Aumentar tus límites",
    description: "Cómo calificar para límites más altos",
    category: "limits-fees",
    order: 2,
    keywords: ["aumentar", "subir", "más alto", "más", "expandir"],
    content: `
# Aumentar tus límites

¿Listo para límites más altos? Así puedes subir de nivel.

## Rutas de actualización

### Estándar → Avanzado

**Requisitos:**
- 30+ días de antigüedad de cuenta
- $5,000+ de volumen exitoso
- Sin problemas de cumplimiento
- Verificación adicional

**Pasos:**
1. Ve a Ajustes > Verificación
2. Haz clic en "Subir a Avanzado"
3. Envía comprobante de domicilio
4. Proporciona información de origen de fondos
5. Espera la revisión (1-3 días)

### Avanzado → Enterprise

**Requisitos:**
- Cuenta empresarial
- Alto volumen de transacciones
- Revisión de cuenta dedicada
- Acuerdo personalizado

**Pasos:**
1. Contacta con enterprise@supabank.xyz
2. Programa una consulta
3. Completa la verificación empresarial
4. Negocia los términos
5. Actualización de cuenta

## Documentos requeridos

### Para Avanzado
- Comprobante de domicilio (factura de servicios reciente)
- Estado de cuenta bancario (últimos 3 meses)
- Declaración de origen de fondos

### Para Enterprise
- Documentos de registro empresarial
- Estados financieros
- Resolución del consejo (si aplica)
- Verificación de beneficiarios finales

## Plazos de aprobación

| Actualización | Tiempo típico |
|---------------|---------------|
| Estándar → Avanzado | 1-3 días hábiles |
| Avanzado → Enterprise | 5-10 días hábiles |

## Consejos para aprobación

Mantén los documentos claros y legibles
Asegúrate de que los nombres coincidan
Usa documentos recientes (< 3 meses)
Completa todos los campos con precisión
Responde rápidamente a las solicitudes
    `.trim(),
  },
  {
    id: "lf-003",
    slug: "fee-structure",
    title: "Estructura de comisiones",
    description: "Desglose completo de todas las comisiones",
    category: "limits-fees",
    order: 3,
    keywords: ["comisiones", "costo", "precios", "cargos", "tarifas"],
    content: `
# Estructura de comisiones

Precios transparentes sin comisiones ocultas.

## Comisiones de conversión

Cuando el cripto se convierte a fiat, se aplican dos comisiones:

1. **Comisión de plataforma**: 2.0% del monto de conversión
2. **Comisión de Bridge**: ~0.1% del monto de conversión

**Comisión total de conversión: ~2.1%**

### Ejemplo de cálculo de comisiones

\`\`\`
Depósito: 1,000 USDC
├── Comisión plataforma: $20.00 (2.0%)
├── Comisión Bridge: ~$1.00 (0.1%)
└── Total comisiones: ~$21.00

Neto recibido: ~$979.00
\`\`\`

### Precios basados en volumen

| Volumen mensual | Comisión plataforma | Total (aprox) |
|-----------------|---------------------|---------------|
| Estándar | 2.0% | ~2.1% |
| Alto volumen | 2.0% | ~2.1% |
| Enterprise ($1M+) | Personalizado | Personalizado |

*La comisión de Bridge puede variar ligeramente según condiciones del mercado*

## Comisiones de pago

| Método | Comisión |
|--------|----------|
| ACH (EE.UU.) | Gratis |
| Wire (EE.UU. doméstico) | $15 |
| Wire (Internacional) | $25 |
| SEPA (Europa) | Gratis |
| SPEI (México) | Gratis |

## Sin comisiones ocultas

**No cobramos por:**
- Creación de cuenta
- Mantenimiento de cuenta
- Recepción de depósitos
- Visualización de monedas
- Atención al cliente
- Acceso a API (empresas)

## Comisiones de terceros

**Tus costos fuera de Supabank:**
- Comisiones de red blockchain (gas)
- Comisión de tu banco por transferencias entrantes
- Comisiones de bancos intermediarios (wires internacionales)
- Conversión de moneda en tu banco

## Ejemplo de cálculo de comisiones

\`\`\`
Depósito: 1,000 USDC
└── Comisión de red: ~$2 (pagada por ti a la blockchain)

Conversión: 1,000 USDC → ~979 USD
└── Comisión plataforma: $20.00 (2%)
└── Comisión Bridge: ~$1.00 (0.1%)
└── Total comisión conversión: ~$21.00

Pago: ~$979 vía ACH
└── Comisión de pago: $0 (ACH gratis)

═══════════════════════
Neto Recibido: ~$979.00
\`\`\`

## Ver tus comisiones

1. Los detalles de transacción muestran todas las comisiones
2. Resumen mensual en Ajustes
3. La exportación incluye desglose de comisiones
    `.trim(),
  },
  {
    id: "lf-004",
    slug: "volume-based-pricing",
    title: "Precios basados en volumen",
    description: "Descuentos para usuarios de alto volumen",
    category: "limits-fees",
    order: 4,
    keywords: ["volumen", "descuento", "precios", "nivel", "ahorro"],
    content: `
# Precios basados en volumen

Mayor volumen significa menores comisiones.

## Niveles de volumen

| Volumen mensual | Comisión plataforma | Total (aprox) |
|-----------------|---------------------|---------------|
| Hasta $100,000 | 2.0% | ~2.1% |
| $100,001 - $500,000 | 2.0% | ~2.1% |
| $500,001 - $1,000,000 | 2.0% | ~2.1% |
| $1,000,000+ | Personalizado | Personalizado |

*Nota: La comisión de plataforma es fija al 2%. La comisión de Bridge (~0.1%) es separada y puede variar.*

## Cómo se calcula el volumen

- Período móvil de 30 días
- Todas las conversiones incluidas
- Actualizado diariamente
- Asignación automática de nivel

## Beneficios por nivel

### Estándar (< $100K)
- 2.0% comisión plataforma (~2.1% total)
- Límites estándar
- Soporte por correo
- Procesamiento regular

### Alto volumen ($100K - $1M)
- 2.0% comisión plataforma (~2.1% total)
- Límites más altos
- Soporte prioritario
- Procesamiento más rápido

### Enterprise ($1M+)
- Tasas de conversión personalizadas
- Límites ilimitados
- Gestor dedicado
- Servicio premium
- Prioridad en API

## Alcanzar niveles más altos

1. Aumenta el volumen de transacciones
2. Mantén actividad constante
3. Sin problemas de cumplimiento
4. Actualización automática

## Seguimiento de volumen

Ver tu volumen:
1. Ve a Ajustes > Límites
2. Ver "Volumen 30 días"
3. Ver progreso del nivel
4. Cambio de nivel estimado

## Precios Enterprise

Para volúmenes superiores a $1M/mes:
- Contacta con enterprise@supabank.xyz
- Negociación de tarifas personalizadas
- Compromisos de volumen
- Acuerdos de SLA
    `.trim(),
  },
  {
    id: "lf-005",
    slug: "enterprise-limits",
    title: "Límites Enterprise",
    description: "Límites de cuenta empresarial de alto volumen",
    category: "limits-fees",
    order: 5,
    keywords: ["enterprise", "empresarial", "corporativo", "ilimitado", "personalizado"],
    content: `
# Límites Enterprise

Límites personalizados para necesidades empresariales de alto volumen.

## Características Enterprise

### Límites personalizados
- Límites de transacción adaptados a necesidades
- Sin topes estándar
- Ajustes basados en volumen
- Términos flexibles

### Soporte dedicado
- Gestor de cuenta asignado
- Acceso telefónico directo
- Manejo prioritario de tickets
- Soporte de integración técnica

### SLA mejorado
- Garantía de 99.9% de disponibilidad
- Procesamiento prioritario
- Liquidaciones más rápidas
- Tiempos de respuesta a incidentes

## Calificar para Enterprise

### Requisitos mínimos
- Volumen mensual: $1,000,000+
- Verificación empresarial completa
- Historial de cumplimiento limpio
- Patrones de transacción estables

### Proceso de solicitud
1. Contacta con enterprise@supabank.xyz
2. Llamada de consulta inicial
3. Revisión de documentación empresarial
4. Evaluación de riesgos
5. Negociación de acuerdo personalizado
6. Actualización de cuenta

## Precios Enterprise

| Servicio | Tarifa Enterprise |
|----------|-------------------|
| Conversión | Desde 0.15% |
| Pagos wire | Reducidos o exentos |
| Llamadas API | Ilimitadas |
| Soporte | Premium incluido |

*Las tarifas finales dependen del volumen y los términos*

## Opciones de integración

### Acceso a API
- Acceso completo a API
- Entorno sandbox
- Documentación técnica
- Soporte de integración

### Soluciones personalizadas
- Opciones de marca blanca
- Reportes personalizados
- Endpoints dedicados
- Operaciones masivas

## Comenzar

¿Listo para Enterprise? Contacta:
- Email: enterprise@supabank.xyz
- Incluye: Nombre de empresa, volumen esperado, caso de uso
    `.trim(),
  },
];
